import { Location } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import * as _ from "lodash";
import { isNil, isNumber } from "lodash";
import { BsModalService } from "ngx-bootstrap/modal";
import { ToastrService } from "ngx-toastr";
import { TrackingService } from "src/app/tracking.service";
import { BackendApiService } from "../backend-api.service";
import { BuyDesoModalComponent } from "../buy-deso-page/buy-deso-modal/buy-deso-modal.component";
import { GlobalVarsService } from "../global-vars.service";
import { NFTEntryResponse, PostEntryResponse } from "deso-protocol";

@Component({
  selector: "place-bid",
  templateUrl: "./place-bid.component.html",
})
export class PlaceBidComponent implements OnInit {
  static PAGE_SIZE = 50;
  static BUFFER_SIZE = 10;
  static WINDOW_VIEWPORT = false;
  static PADDING = 0.5;

  @Input() postHashHex: string;
  @Input() post: PostEntryResponse;
  @Output() closeModal = new EventEmitter<any>();
  @Output() changeTitle = new EventEmitter<string>();

  bidAmountDeSo: number;
  bidAmountUSD: number;
  selectedSerialNumber: NFTEntryResponse = null;
  availableCount: number;
  availableSerialNumbers: NFTEntryResponse[];
  biddableSerialNumbers: NFTEntryResponse[];
  serialNumbersForTab: NFTEntryResponse[];
  highBid: number = null;
  lowBid: number = null;
  loading = true;
  isSelectingSerialNumber = true;
  saveSelectionDisabled = false;
  showSelectedSerialNumbers = false;
  showBuyNowConfirmation = false;
  placingBids: boolean = false;
  errors: string[] = [];
  SN_FIELD = "SerialNumber";
  HIGH_BID_FIELD = "HighestBidAmountNanos";
  MIN_BID_FIELD = "MinBidAmountNanos";
  sortByField = this.SN_FIELD;
  sortByOrder: "desc" | "asc" = "asc";
  minBidCurrency: string = "USD";
  minBidInput: number = 0;
  BID_TAB = "Bid";
  BUY_TAB = "Buy Now";
  tabs = [this.BUY_TAB, this.BID_TAB];
  activeTab = this.BUY_TAB;
  showTabs = false;
  serialNumberSelectColumns: { high?: string; min?: string; buyNow?: string; secondaryIndicator: string };

  constructor(
    public globalVars: GlobalVarsService,
    private backendApi: BackendApiService,
    private modalService: BsModalService,
    private router: Router,
    private toastr: ToastrService,
    private location: Location,
    private translocoService: TranslocoService,
    private tracking: TrackingService
  ) {}

  ngOnInit(): void {
    this.backendApi
      .GetNFTCollectionSummary(
        this.globalVars.localNode,
        this.globalVars.loggedInUser?.PublicKeyBase58Check,
        this.post.PostHashHex
      )
      .subscribe((res) => {
        this.availableSerialNumbers = _.values(res.SerialNumberToNFTEntryResponse);
        this.availableCount = res.NFTCollectionResponse.PostEntryResponse.NumNFTCopiesForSale;
        this.getBiddableSerialNumbers();
        const hasAuctionNFTs = _.filter(this.biddableSerialNumbers, { IsBuyNow: false }).length > 0;
        const hasBuyNowNFTs = _.filter(this.biddableSerialNumbers, { IsBuyNow: true }).length > 0;
        // Only show tabs if there are buy now SNs
        this.showTabs = hasBuyNowNFTs;
        // If there are Buy Now SNs available for purchase, set the tab to buy now, otherwise default to auctions
        this.activeTab = hasBuyNowNFTs ? this.BUY_TAB : this.BID_TAB;
        this.tabClicked(this.activeTab);
      })
      .add(() => (this.loading = false));
  }

  tabClicked(tabName: string) {
    this.activeTab = tabName;
    this.serialNumberSelectColumns =
      this.activeTab === this.BID_TAB
        ? { high: "Highest Bid", min: "Min Bid Amount", secondaryIndicator: "" }
        : { buyNow: "Buy Now Price", secondaryIndicator: "" };
    this.getBiddableSerialNumbers();
  }

  getBiddableSerialNumbers() {
    this.biddableSerialNumbers = _.orderBy(
      this.availableSerialNumbers
        .filter(
          (nftEntryResponse) =>
            nftEntryResponse.OwnerPublicKeyBase58Check !== this.globalVars.loggedInUser?.PublicKeyBase58Check
        )
        .filter((nftEntryResponse) => {
          // If we're in they "buy now" tab, filter to only buy now SNs
          return this.activeTab === this.BID_TAB || (nftEntryResponse.IsBuyNow && this.BUY_TAB);
        }),
      [this.sortByField],
      [this.sortByOrder]
    ) as Array<NFTEntryResponse>;
  }

  updateBidAmountUSD(deSoAmount) {
    this.bidAmountUSD = parseFloat(this.globalVars.nanosToUSDNumber(deSoAmount * 1e9).toFixed(2));
    this.setErrors();
  }

  updateBidAmountDeSo(usdAmount) {
    this.bidAmountDeSo = Math.trunc(this.globalVars.usdToNanosNumber(usdAmount)) / 1e9;
    this.setErrors();
  }

  setErrors(): void {
    this.errors = [];
    if (this.bidAmountDeSo * 1e9 > this.globalVars.loggedInUser.BalanceNanos) {
      this.errors.push(`You do not have ${this.bidAmountDeSo} DESO to fulfill this bid.`);
    }
    if (this.bidAmountDeSo * 1e9 === 0) {
      this.errors.push(`You must bid more than 0 DESO`);
    } else if (this.selectedSerialNumber?.MinBidAmountNanos > this.bidAmountDeSo * 1e9) {
      this.errors.push(
        `Your bid does not meet the minimum bid requirement of ${this.globalVars.nanosToDeSo(
          this.selectedSerialNumber.MinBidAmountNanos
        )} DESO (${this.globalVars.nanosToUSD(this.selectedSerialNumber.MinBidAmountNanos, 2)})`
      );
    }
  }

  getPlaceBidText() {
    const translationKey = this.placingBids ? "place_bid.placing_bid" : "place_bid.place_a_bid";
    return this.translocoService.translate(translationKey);
  }

  placeBid() {
    this.setErrors();
    if (this.errors.length) {
      return;
    }
    this.saveSelectionDisabled = true;
    this.placingBids = true;
    this.backendApi
      .CreateNFTBid(
        this.globalVars.loggedInUser?.PublicKeyBase58Check,
        this.post.PostHashHex,
        this.selectedSerialNumber.SerialNumber,
        Math.trunc(this.bidAmountDeSo * 1e9)
      )
      .subscribe(
        (res) => {
          this.tracking.log("nft-place-bid : submit", {
            postHashHex: this.post.PostHashHex,
            authorUsername: this.post.ProfileEntryResponse?.Username,
            authorPublicKey: this.post.ProfileEntryResponse?.PublicKeyBase58Check,
            hasText: this.post.Body.length > 0,
            hasImage: (this.post.ImageURLs?.length ?? 0) > 0,
            hasVideo: (this.post.VideoURLs?.length ?? 0) > 0,
            hasEmbed: !!this.post.PostExtraData?.EmbedVideoURL,
            hasUnlockable: this.post.HasUnlockable,
          });
          if (!this.globalVars.isMobile()) {
            // Hide this modal and open the next one.
            this.closeModal.emit("bid placed");
          } else {
            this.location.back();
          }
          this.showToast();
        },
        (err) => {
          console.error(err);
          const parsedError = this.backendApi.parseErrorMessage(err);
          this.globalVars._alertError(parsedError);
          this.tracking.log("nft-place-bid : submit", { error: parsedError });
        }
      )
      .add(() => {
        this.placingBids = false;
        this.saveSelectionDisabled = false;
      });
  }

  openBuyDeSoModal() {
    this.modalService.show(BuyDesoModalComponent, {
      class: "modal-dialog-centered buy-deso-modal",
      backdrop: "static",
    });
  }

  navigateToBuyDeSo(): void {
    this.closeModal.emit();
    this.openBuyDeSoModal();
  }

  showToast(): void {
    const link = `/${this.globalVars.RouteNames.NFT}/${this.post.PostHashHex}`;
    this.toastr.show(`Bid placed<a href="${link}" class="toast-link cursor-pointer">View</a>`, null, {
      toastClass: "info-toast",
      enableHtml: true,
      positionClass: "toast-bottom-center",
    });
  }

  saveSelection(): void {
    if (!this.saveSelectionDisabled) {
      this.isSelectingSerialNumber = false;
      this.highBid = this.selectedSerialNumber.HighestBidAmountNanos;
      this.lowBid = this.selectedSerialNumber.LowestBidAmountNanos;
      if (this.activeTab === this.BID_TAB) {
        this.showSelectedSerialNumbers = true;
        this.changeTitle.emit("Set your bid");
        this.setErrors();
      } else {
        this.showBuyNowConfirmation = true;
        this.changeTitle.emit("Confirm your purchase");
        this.setErrors();
      }
    }
  }

  goBackToSerialSelection(): void {
    this.isSelectingSerialNumber = true;
    this.showSelectedSerialNumbers = false;
    this.showBuyNowConfirmation = false;
    this.changeTitle.emit("Choose an edition");
    this.highBid = null;
    this.lowBid = null;
    this.selectedSerialNumber = null;
  }

  selectSerialNumber(serialNumber: NFTEntryResponse) {
    this.selectedSerialNumber = serialNumber;
    this.saveSelection();
  }

  deselectSerialNumber() {
    if (this.placingBids) {
      return;
    }
    this.selectedSerialNumber = null;
    this.showSelectedSerialNumbers = false;
    this.highBid = null;
    this.lowBid = null;
    this.setErrors();
  }

  lastPage = null;

  getPage(page: number) {
    if (!isNil(this.lastPage) && page > this.lastPage) {
      return [];
    }
    const startIdx = page * PlaceBidComponent.PAGE_SIZE;
    const endIdx = (page + 1) * PlaceBidComponent.PAGE_SIZE;

    return new Promise((resolve, reject) => {
      resolve(this.biddableSerialNumbers.slice(startIdx, Math.min(endIdx, this.biddableSerialNumbers.length)));
    });
  }

  updateBidSort(sortField: string) {
    if (this.sortByField === sortField) {
      this.sortByOrder = this.sortByOrder === "asc" ? "desc" : "asc";
    } else {
      this.sortByOrder = "asc";
    }
    this.sortByField = sortField;
    this.biddableSerialNumbers = _.orderBy(
      this.biddableSerialNumbers,
      [this.sortByField],
      [this.sortByOrder]
    ) as Array<NFTEntryResponse>;
  }

  bidAmountUSDFormatted() {
    return isNumber(this.bidAmountUSD) ? `~${this.globalVars.formatUSD(this.bidAmountUSD, 0)}` : "";
  }

  bidAmountDeSoFormatted() {
    return isNumber(this.bidAmountDeSo) ? `~${this.bidAmountDeSo.toFixed(2)} $DESO` : "";
  }

  bidAmountOtherCurrencyFormatted() {
    if (this.minBidCurrency === "DESO") {
      return this.bidAmountUSDFormatted();
    } else {
      return this.bidAmountDeSoFormatted();
    }
  }

  updateBidAmount(amount: number) {
    if (this.minBidCurrency === "DESO") {
      this.bidAmountDeSo = amount;
      this.updateBidAmountUSD(amount);
    } else {
      this.bidAmountUSD = amount;
      this.updateBidAmountDeSo(amount);
    }
  }
}
